import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import axiosInstance from "../utils/axiosInstance";
import Select from "react-select";
import ReactModal from "react-modal";
import { toast } from "react-toastify";

import MonthChart from "../components/Dashboard/MonthChart";
import DayChart from "../components/Dashboard/DayChart";
import CountryMap from "../components/Dashboard/CountryMap";
import LeadsTable from "../components/Dashboard/LeadsTable";
import LinkedContextDialog from "../components/LinkedContext/LinkedContextDialog";

import OmiOliChart from "../components/Dashboard/OmiOliChart";
import { getSelectedBrandCode, shouldDefaultDashboardMinRelevanceToFifty } from "../utils/brandContext";

ReactModal.setAppElement("#root");

const OP_OPTIONS = [
    { value: "eq", label: "Equals" },
    { value: "ne", label: "Does not equal" },
    { value: "has", label: "Contains data" },
    { value: "not_has", label: "Does not contain data" },
    { value: "contains", label: "Contains" },
    { value: "not_contains", label: "Does not contain" },
    { value: "begins", label: "Begins with" },
    { value: "not_begins", label: "Does not begin with" },
    { value: "ends", label: "Ends with" },
    { value: "not_ends", label: "Does not end with" },
];

const isNoop = (f) => !f || (f.op === "eq" && (!f.values || f.values.length === 0));
const matchOne = (raw, f) => {
    const s = (raw ?? "").toString().trim().toLowerCase();
    const vals = (f.values || []).map((v) => v.value.toLowerCase());
    switch (f.op) {
        case "has":
            return s.length > 0;
        case "not_has":
            return s.length === 0;
        case "eq":
            return vals.length === 0 ? true : vals.includes(s);
        case "ne":
            return vals.length === 0 ? true : !vals.includes(s);
        case "contains":
            return vals.length === 0 ? true : vals.some((v) => s.includes(v));
        case "not_contains":
            return vals.length === 0 ? true : vals.every((v) => !s.includes(v));
        case "begins":
            return vals.length === 0 ? true : vals.some((v) => s.startsWith(v));
        case "not_begins":
            return vals.length === 0 ? true : vals.every((v) => !s.startsWith(v));
        case "ends":
            return vals.length === 0 ? true : vals.some((v) => s.endsWith(v));
        case "not_ends":
            return vals.length === 0 ? true : vals.every((v) => !s.endsWith(v));
        default:
            return true;
    }
};
const applyFilter = (rows, field, f) => (isNoop(f) ? rows : rows.filter((r) => matchOne(r[field], f)));
const getLocalYmd = (value) => {
    if (!value) return "";
    const s = String(value).trim();
    const m = s.match(/^(\d{4}-\d{2}-\d{2})/);
    if (m) return m[1];
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    const y = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${y}-${mm}-${dd}`;
};

export default function DashboardDomain() {
    const user = localStorage.getItem("username");
    const email = localStorage.getItem("email");
    const initialBrandCode = getSelectedBrandCode();

    const [allRawData, setAllRawData] = useState({ chart_data_raw: [], detailed_leads_data: [] });
    const [brandCode, setBrandCode] = useState(initialBrandCode);
    const [minRel, setMinRel] = useState(shouldDefaultDashboardMinRelevanceToFifty(initialBrandCode) ? 50 : 0);
    const [loadingData, setLoadingData] = useState(true);
    const [tableRows, setTableRows] = useState([]);
    const [tableTotal, setTableTotal] = useState(0);
    const [tableTotalPages, setTableTotalPages] = useState(1);
    const [isLinkedDialogOpen, setIsLinkedDialogOpen] = useState(false);
    const [linkedLoading, setLinkedLoading] = useState(false);
    const [linkedRecord, setLinkedRecord] = useState(null);
    const [linkedContacts, setLinkedContacts] = useState([]);
    const [blacklistBusy, setBlacklistBusy] = useState(false);

    const [selectedMonth, setSelectedMonth] = useState(null); // YYYY-MM
    const [selectedDay, setSelectedDay] = useState(null); // YYYY-MM-DD
    const [currentPage, setCurrentPage] = useState(0);

    const [searchTerm, setSearchTerm] = useState("");
    const [countryFilter, setCountryFilter] = useState({ op: "eq", values: [] });
    const [categoryFilter, setCategoryFilter] = useState({ op: "eq", values: [] });
    const [selectedCRM, setSelectedCRM] = useState({ value: "*", label: "All CRM Statuses" });

    const crmOptions = useMemo(
        () => [
            { value: "*", label: "All CRM Statuses" },
            { value: "hasOwner", label: "Recorded in CRM" },
            { value: "noOwner", label: "Not Recorded in CRM" },
        ],
        []
    );

    const [sortField, setSortField] = useState("created_at");
    const [sortOrder, setSortOrder] = useState("desc");

    const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
    const [recordToDelete, setRecordToDelete] = useState(null);
    const [recordToUpdate, setRecordToUpdate] = useState({});
    const [recordDetails, setRecordDetails] = useState({});

    const [emailButtonDisabled, setEmailButtonDisabled] = useState(false);
    const [expandButtonDisabled, setExpandButtonDisabled] = useState(false);
    const [leadButtonDisabled, setLeadButtonDisabled] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);

    const recordsPerPage = 30;
    const encodeVals = (vals = []) =>
        vals
            .map((v) => (v?.value ?? "").toString().trim())
            .filter(Boolean)
            .join(",");

    const monthStartDate = (ym) => `${ym}-01`;
    const monthEndDate = (ym) => {
        const [yStr, mStr] = String(ym || "").split("-");
        const y = Number(yStr);
        const m = Number(mStr);
        if (!y || !m) return "";
        const d = new Date(y, m, 0).getDate();
        return `${ym}-${String(d).padStart(2, "0")}`;
    };
    const defaultStartDate = () => {
        const now = new Date();
        const d = new Date(now.getFullYear(), now.getMonth() - 11, 1);
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
    };

    const buildTableQueryParams = useCallback((pageToLoad = currentPage + 1, pageSize = recordsPerPage) => {
        let startDate = defaultStartDate();
        let endDate;
        if (selectedDay) {
            startDate = selectedDay;
            endDate = selectedDay;
        } else if (selectedMonth) {
            startDate = monthStartDate(selectedMonth);
            endDate = monthEndDate(selectedMonth);
        }

        return {
            page: pageToLoad,
            page_size: pageSize,
            sort_by: sortField || "created_at",
            sort_order: sortField ? sortOrder : "desc",
            q: searchTerm.trim() || undefined,
            min_relevance: Number(minRel) || 0,
            category_op: isNoop(categoryFilter) ? undefined : categoryFilter.op,
            category_vals: isNoop(categoryFilter) ? undefined : encodeVals(categoryFilter.values),
            country_op: isNoop(countryFilter) ? undefined : countryFilter.op,
            country_vals: isNoop(countryFilter) ? undefined : encodeVals(countryFilter.values),
            crm_status: selectedCRM.value === "*" ? undefined : selectedCRM.value,
            date_field: "created_at",
            start_date: startDate || undefined,
            end_date: endDate || undefined,
        };
    }, [
        currentPage,
        recordsPerPage,
        selectedDay,
        selectedMonth,
        sortField,
        sortOrder,
        searchTerm,
        minRel,
        categoryFilter,
        countryFilter,
        selectedCRM,
    ]);

    const fetchSummaryData = useCallback(async () => {
        const res = await axiosInstance.get("/record/all_leads_data");
        const payload = res?.data || {};
        const rows = Array.isArray(payload?.detailed_leads_data) ? payload.detailed_leads_data : [];
        setAllRawData({ chart_data_raw: rows, detailed_leads_data: rows });
    }, [brandCode]);

    const fetchTableData = useCallback(async (pageToLoad = currentPage + 1) => {
        const resp = await axiosInstance.get("/record/records", {
            params: buildTableQueryParams(pageToLoad),
        });
        const payload = resp?.data || {};
        setTableRows(Array.isArray(payload?.items) ? payload.items : []);
        setTableTotal(Number(payload?.total || 0));
        setTableTotalPages(Number(payload?.total_pages || 1));
    }, [currentPage, buildTableQueryParams, brandCode]);

    const fetchAllFilteredRecords = async () => {
        const pageSize = 1000;
        const first = await axiosInstance.get("/record/records", {
            params: buildTableQueryParams(1, pageSize),
        });
        const p = first?.data || {};
        const totalPagesAll = Number(p?.total_pages || 1);
        const out = Array.isArray(p?.items) ? [...p.items] : [];
        for (let pno = 2; pno <= totalPagesAll; pno += 1) {
            const resp = await axiosInstance.get("/record/records", {
                params: buildTableQueryParams(pno, pageSize),
            });
            const items = Array.isArray(resp?.data?.items) ? resp.data.items : [];
            out.push(...items);
        }
        return out;
    };

    useEffect(() => {
        if (!user) {
            setLoadingData(false);
            return;
        }
        setLoadingData(true);
        fetchSummaryData()
            .catch(() => {
                setAllRawData({ chart_data_raw: [], detailed_leads_data: [] });
                setTableRows([]);
                setTableTotal(0);
                setTableTotalPages(1);
            })
            .finally(() => setLoadingData(false));
    }, [user, fetchSummaryData, brandCode]);

    useEffect(() => {
        const syncBrand = () => {
            const next = getSelectedBrandCode();
            setBrandCode(next);
            setMinRel(shouldDefaultDashboardMinRelevanceToFifty(next) ? 50 : 0);
            setSelectedMonth(null);
            setSelectedDay(null);
            setCurrentPage(0);
        };
        window.addEventListener("brandUpdated", syncBrand);
        window.addEventListener("storage", syncBrand);
        return () => {
            window.removeEventListener("brandUpdated", syncBrand);
            window.removeEventListener("storage", syncBrand);
        };
    }, []);

    useEffect(() => {
        if (!user || loadingData) return;
        fetchTableData(currentPage + 1).catch(() => toast.error("Failed to fetch table records."));
    }, [user, loadingData, currentPage, fetchTableData]);

    const countryOptions = useMemo(() => {
        const uniq = Array.from(new Set(allRawData.detailed_leads_data.map((r) => r.country).filter(Boolean)));
        return uniq.map((c) => ({ value: c, label: c }));
    }, [allRawData.detailed_leads_data]);

    const categoryOptions = useMemo(() => {
        const uniq = Array.from(
            new Set(allRawData.detailed_leads_data.map((r) => r.business_category).filter(Boolean))
        );
        return uniq.map((c) => ({ value: c, label: c }));
    }, [allRawData.detailed_leads_data]);

    // ---- Helper: base filters shared across views (no month/day, no country) ----
    const baseFiltered = useMemo(() => {
        const q = searchTerm.trim().toLowerCase();
        let temp = q
            ? allRawData.detailed_leads_data.filter((r) => {
                const d = (r.domain || "").toString().toLowerCase();
                const c = (r.company_name || "").toString().toLowerCase();
                return d.includes(q) || c.includes(q);
            })
            : [...allRawData.detailed_leads_data];

        // relevance
        temp = temp.filter((lead) => (lead.relevance ?? 0) >= minRel);
        // category
        temp = applyFilter(temp, "business_category", categoryFilter);
        // CRM
        if (selectedCRM.value !== "*") {
            temp = temp.filter((r) => (selectedCRM.value === "hasOwner" ? !!r.dynamics_owner : !r.dynamics_owner));
        }
        return temp;
    }, [allRawData.detailed_leads_data, searchTerm, minRel, categoryFilter, selectedCRM]);

    // ---- Add temporal selection for views that need it ----
    const withTemporal = useMemo(() => {
        let temp = [...baseFiltered];
        if (selectedDay) {
            temp = temp.filter((lead) => getLocalYmd(lead.created_date ?? lead.created_at) === selectedDay);
        } else if (selectedMonth) {
            temp = temp.filter((lead) => getLocalYmd(lead.created_date ?? lead.created_at).substring(0, 7) === selectedMonth);
        }
        return temp;
    }, [baseFiltered, selectedMonth, selectedDay]);

    useEffect(
        () => setCurrentPage(0),
        [selectedMonth, selectedDay, countryFilter, searchTerm, minRel, categoryFilter, selectedCRM, sortField, sortOrder]
    );

    const filteredCount = tableTotal;
    const absoluteTotal = allRawData.detailed_leads_data.length;
    const pageCount = tableTotalPages || 1;
    const displayedRecords = tableRows;

    // ---- Month chart: apply country filter, ignore temporal selection, aggregate last 12 months ----
    const { labels: monthLabels, counts: monthCounts, monthYAxisMax } = useMemo(() => {
        const start = new Date();
        start.setDate(1);
        start.setMonth(start.getMonth() - 11);
        const months = Array.from({ length: 12 }, (_, i) => {
            const d = new Date(start.getFullYear(), start.getMonth() + i, 1);
            return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
        });

        const baseForMonths = applyFilter(baseFiltered, "country", countryFilter);

        const bucket = {};
        for (const r of baseForMonths) {
            const key = getLocalYmd(r.created_date ?? r.created_at).substring(0, 7);
            if (!months.includes(key)) continue;
            bucket[key] = (bucket[key] || 0) + 1;
        }
        const counts = months.map((m) => bucket[m] || 0);
        const max = counts.length ? Math.max(...counts) : 0;
        return { labels: months, counts, monthYAxisMax: Math.ceil(max / 10) * 10 };
    }, [baseFiltered, countryFilter]);

    // ---- Day chart: apply country and selected month ----
    const { dayKeys, dayCounts, dayYAxisMax } = useMemo(() => {
        if (!selectedMonth) return { dayKeys: [], dayCounts: [], dayYAxisMax: 0 };
        const [yStr, mStr] = selectedMonth.split("-");
        const y = Number(yStr),
            m = Number(mStr);
        const daysInMonth = new Date(y, m, 0).getDate();
        const keys = Array.from({ length: daysInMonth }, (_, i) => `${selectedMonth}-${String(i + 1).padStart(2, "0")}`);

        let baseDays = applyFilter(baseFiltered, "country", countryFilter);
        baseDays = baseDays.filter((r) => getLocalYmd(r.created_date ?? r.created_at).substring(0, 7) === selectedMonth);

        const bucket = {};
        for (const r of baseDays) {
            const k = getLocalYmd(r.created_date ?? r.created_at);
            bucket[k] = (bucket[k] || 0) + 1;
        }
        const counts = keys.map((k) => bucket[k] || 0);
        const max = counts.length ? Math.max(...counts) : 0;
        return { dayKeys: keys, dayCounts: counts, dayYAxisMax: Math.ceil(max / 10) * 10 };
    }, [baseFiltered, countryFilter, selectedMonth]);

    // ---- Map counts: base + temporal (month/day), NO country filter ----
    const countryCounts = useMemo(() => {
        const out = {};
        for (const r of withTemporal) {
            const name = (r.country || "").trim();
            if (!name) continue;
            out[name] = (out[name] || 0) + 1;
        }
        return out;
    }, [withTemporal]);

    const selectedCountryName = useMemo(() => {
        if (countryFilter.op === "eq" && countryFilter.values && countryFilter.values.length === 1) {
            return countryFilter.values[0].value;
        }
        return null;
    }, [countryFilter]);

    // ---- Actions / handlers ----
    function handleSort(field) {
        if (sortField !== field) {
            setSortField(field);
            setSortOrder("asc");
        } else {
            if (sortOrder === "asc") setSortOrder("desc");
            else if (sortOrder === "desc") setSortField(null);
        }
    }

    async function handleUpdateRecord() {
        try {
            await axiosInstance.put(`/record/records/${recordToUpdate.record_id}`, recordToUpdate);
            toast.success("Record updated successfully");
            setIsUpdateModalOpen(false);
            setLoadingData(true);
            await Promise.all([fetchSummaryData(), fetchTableData(currentPage + 1)]);
            setLoadingData(false);
        } catch (err) {
            toast.error(err.response?.data?.message || "Failed to update record.");
            setLoadingData(false);
        }
    }

    async function handleDeleteRecord() {
        if (!recordToDelete) return;
        try {
            await axiosInstance.delete(`/record/records/${recordToDelete.record_id}`);
            toast.success("Record deleted successfully");
            setIsDeleteModalOpen(false);
            setLoadingData(true);
            await Promise.all([fetchSummaryData(), fetchTableData(currentPage + 1)]);
            setLoadingData(false);
        } catch (err) {
            toast.error(err.response?.data?.message || "Failed to delete record.");
            setLoadingData(false);
        }
    }

    function handleInputChange(e, updateMode = false) {
        const { name, value } = e.target;
        if (updateMode) setRecordToUpdate((prev) => ({ ...prev, [name]: value }));
    }

    function formatDate(date) {
        if (!date) return "";
        const localDate = new Date(date.replace(/ GMT$/, ""));
        const day = String(localDate.getDate()).padStart(2, "0");
        const month = localDate.toLocaleString("en-AU", { month: "short" });
        const year = localDate.getFullYear();
        const hours = String(localDate.getHours()).padStart(2, "0");
        const minutes = String(localDate.getMinutes()).padStart(2, "0");
        return `${day} ${month}, ${year} ${hours}:${minutes}`;
    }

    async function handleEmailData() {
        const allFiltered = await fetchAllFilteredRecords();
        const formattedRecords = allFiltered.map((rec) => ({
            ...rec,
            created_at: formatDate(rec.created_at),
            expanded_at: formatDate(rec.expanded_at),
        }));
        setEmailButtonDisabled(true);
        try {
            const resp = await axiosInstance.post("/record/send_email", { email, records: formattedRecords });
            if (resp.status === 200) toast.success(resp.data.message || "Email sent successfully");
            else toast.error(resp.data.message || "Failed to send email");
        } catch (err) {
            toast.error("Failed to send email: " + (err.response?.data?.message || err.message));
        } finally {
            setEmailButtonDisabled(false);
        }
    }

    const handleRedo = async () => {
        const allFiltered = await fetchAllFilteredRecords();
        const domainIds = allFiltered.map((rec) => rec.record_id);
        if (domainIds.length === 0) {
            toast.error("No records to redo");
            return;
        }

        setExpandButtonDisabled(true);
        try {
            const response = await axiosInstance.post("/record/forceEnhanceExpand", { domain_ids: domainIds });
            if (response.status === 202) {
                toast.success(response.data.message || "Domains added to redo successfully");
            } else {
                toast.error(response.data.message || "Failed to add records to redo");
            }
        } catch (err) {
            toast.error("Failed to add records to redo" + err.response?.data?.message);
        } finally {
            setExpandButtonDisabled(false);
        }
    };

    async function handleCreateLeads() {
        const allFiltered = await fetchAllFilteredRecords();
        const ids = allFiltered.map((r) => r.record_id);
        if (!ids.length) {
            toast.info("No records selected");
            return;
        }
        setLeadButtonDisabled(true);
        try {
            await axiosInstance.post("/record/create_leads_async", { record_ids: ids });
            toast.success("Lead creation started in background.");
        } catch (err) {
            const msg = err.response?.data?.error || err.message || "Unknown error";
            toast.error(`Failed to create leads: ${msg}`);
        } finally {
            setLeadButtonDisabled(false);
        }
    }

    async function handleSingleRedo(recordId) {
        setExpandButtonDisabled(true);
        try {
            const resp = await axiosInstance.post("/record/forceEnhanceExpand", { domain_ids: [recordId] });
            if (resp.status === 202) toast.success(resp.data.message || "Domain added to redo");
            else toast.error(resp.data.message || "Failed to add to redo");
        } catch (err) {
            toast.error("Failed to add to redo" + (err.response?.data?.message ? `: ${err.response.data.message}` : ""));
        } finally {
            setExpandButtonDisabled(false);
        }
    }

    async function handleCreateSingleLead(recordId) {
        setLeadButtonDisabled(true);
        try {
            const { data } = await axiosInstance.post("/record/create_leads", { record_ids: [recordId] });
            const { created = [], skipped = [], errors = [] } = data;
            const parts = [];
            if (created.length) parts.push(`${created.length} lead${created.length > 1 ? "s" : ""} created`);
            if (skipped.length) parts.push(`${skipped.length} skipped`);
            if (errors.length) parts.push(`${errors.length} error${errors.length > 1 ? "s" : ""}`);
            toast.success(parts.join(", ") || "Done");
        } catch (err) {
            toast.error(`Failed to create lead: ${err.response?.data?.error || err.message || "Unknown error"}`);
        } finally {
            setLeadButtonDisabled(false);
        }
    }

    const openLinkedContactsDialog = async (record) => {
        if (!record?.record_id) return;
        setIsLinkedDialogOpen(true);
        setLinkedLoading(true);
        setLinkedRecord(record);
        setLinkedContacts([]);
        try {
            const resp = await axiosInstance.get(`/record/records/${record.record_id}/contacts`);
            setLinkedContacts(Array.isArray(resp?.data) ? resp.data : []);
        } catch (err) {
            console.error(err);
            toast.error("Failed to load linked contacts.");
        } finally {
            setLinkedLoading(false);
        }
    };

    const addDomainToBlacklist = async (domain) => {
        const d = (domain || "").trim();
        if (!d) return;
        setBlacklistBusy(true);
        try {
            await axiosInstance.post("/blacklist/blacklists", { domain: d });
            toast.success(`Added "${d}" to blacklist.`);
        } catch (err) {
            toast.error(err.response?.data?.error || "Failed to add blacklist domain.");
        } finally {
            setBlacklistBusy(false);
        }
    };

    if (loadingData) {
        return (
            <div className="flex h-screen w-full items-center justify-center bg-gray-100">
                <div className="flex flex-col items-center gap-4">
                    <div className="h-10 w-10 animate-spin rounded-full border-4 border-slate-200 border-t-slate-900" />
                    <p className="text-sm sm:text-base font-semibold text-slate-700">Loading domain data...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-100 px-3 sm:px-4 md:px-6 py-6">
            <div className="w-full max-w-full mx-auto">
                {user && (
                    <div className="flex flex-col gap-5 sm:gap-6">
                        <section className="rounded-2xl border border-slate-200 bg-white shadow-sm p-4">
                            <div className="p-2">
                                <h2 className="text-lg sm:text-xl font-extrabold text-slate-900 tracking-tight">
                                    OMI & OLI
                                </h2>
                                <p className="mt-1 text-sm text-slate-600">
                                    Opportunity Mapping Index (OMI) & Opportunity Leverage Index (OLI)
                                </p>
                            </div>
                            <OmiOliChart key={`omi-${brandCode}`} />
                        </section>
                        {/* Controls + Charts */}
                        <section className="rounded-2xl border border-slate-200 bg-white shadow-sm">
                            <div className="p-4 sm:p-6">
                                <div className="flex items-start justify-between gap-4 mb-4">
                                    <div>
                                        <h2 className="text-lg sm:text-xl font-extrabold text-slate-900 tracking-tight">
                                            Domain Activity
                                        </h2>
                                        <p className="mt-1 text-sm text-slate-600">
                                            Adjust relevance and drill down by month/day.
                                        </p>
                                    </div>

                                    <div className="shrink-0 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-right">
                                        <div className="text-[11px] uppercase tracking-wide text-slate-500">Min relevance</div>
                                        <div className="text-sm font-extrabold text-slate-900">{minRel}%</div>
                                    </div>
                                </div>

                                <div className="mb-4">
                                    <label htmlFor="rel-slider" className="block text-sm font-semibold text-slate-700 mb-2">
                                        Min. relevance: <span className="font-extrabold text-slate-900">{minRel}%</span>
                                    </label>
                                    <input
                                        id="rel-slider"
                                        type="range"
                                        min="0"
                                        max="100"
                                        step="1"
                                        value={minRel}
                                        onChange={(e) => {
                                            setMinRel(Number(e.target.value));
                                            setSelectedMonth(null);
                                            setSelectedDay(null);
                                            setCurrentPage(0);
                                        }}
                                        className="w-full accent-slate-900"
                                    />
                                </div>

                                <div className="flex justify-center px-1 sm:px-4">
                                    <MonthChart
                                        labels={monthLabels}
                                        counts={monthCounts}
                                        minRel={minRel}
                                        yAxisMax={monthYAxisMax}
                                        onSelect={(m) => {
                                            setSelectedMonth(m);
                                            setSelectedDay(null);
                                            setCurrentPage(0);
                                            if (m) toast.info(`Zoomed into ${m} (relevance >= ${minRel}%)`);
                                        }}
                                    />
                                </div>

                                {(selectedMonth || selectedDay) && (
                                    <div className="flex flex-wrap items-center gap-2 mt-4">
                                        {selectedMonth && (
                                            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-900 text-white text-sm font-semibold">
                                                Month: {selectedMonth}
                                                <button
                                                    onClick={() => {
                                                        setSelectedMonth(null);
                                                        setSelectedDay(null);
                                                        setCurrentPage(0);
                                                    }}
                                                    className="ml-1 rounded-full px-2 py-0.5 bg-white/10 hover:bg-white/20 transition"
                                                    title="Clear month"
                                                >
                                                    X
                                                </button>
                                            </span>
                                        )}
                                        {selectedDay && (
                                            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-600 text-white text-sm font-semibold">
                                                Day: {selectedDay}
                                                <button
                                                    onClick={() => {
                                                        setSelectedDay(null);
                                                        setCurrentPage(0);
                                                    }}
                                                    className="ml-1 rounded-full px-2 py-0.5 bg-white/10 hover:bg-white/20 transition"
                                                    title="Clear day"
                                                >
                                                    X
                                                </button>
                                            </span>
                                        )}
                                    </div>
                                )}

                                {selectedMonth && (
                                    <div className="mt-6 flex justify-center px-1 sm:px-4">
                                        <DayChart
                                            month={selectedMonth}
                                            fullDayKeys={dayKeys}
                                            counts={dayCounts}
                                            minRel={minRel}
                                            yAxisMax={dayYAxisMax}
                                            onSelect={(dayKey) => {
                                                setSelectedDay(dayKey);
                                                setCurrentPage(0);
                                                if (dayKey) toast.info(`Showing ${dayKey} (relevance >= ${minRel}%)`);
                                            }}
                                        />
                                    </div>
                                )}
                            </div>
                        </section>

                        {/* Map */}
                        <section className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
                            <div className="p-4 sm:p-6">
                                <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                                    <div>
                                        <h2 className="text-lg sm:text-xl font-extrabold text-slate-900 tracking-tight">
                                            Map View by Country
                                        </h2>
                                        <p className="mt-1 text-sm text-slate-600">
                                            Counts reflect current filters
                                            {selectedMonth ? ` for ${selectedMonth}` : ""}
                                            {selectedDay ? ` on ${selectedDay}` : ""}. Click a country to toggle a filter.
                                        </p>
                                    </div>

                                    {selectedCountryName && (
                                        <button
                                            onClick={() => setCountryFilter({ op: "eq", values: [] })}
                                            className="inline-flex items-center justify-center rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-semibold text-slate-800 hover:bg-slate-100 transition"
                                        >
                                            Clear Country
                                        </button>
                                    )}
                                </div>

                                <CountryMap
                                    countryCounts={countryCounts}
                                    selectedCountryName={selectedCountryName}
                                    onSelectCountry={(name) => {
                                        if (name) setCountryFilter({ op: "eq", values: [{ value: name, label: name }] });
                                        else setCountryFilter({ op: "eq", values: [] });
                                        setCurrentPage(0);
                                    }}
                                />
                            </div>
                        </section>

                        {/* Actions + Filters + Table */}
                        <section className="rounded-2xl border border-slate-200 bg-white shadow-sm">
                            <div className="p-4 sm:p-6">
                                {/* Actions */}
                                <div className="grid grid-cols-2 md:grid-cols-5 gap-2 sm:gap-3 mb-5">
                                    <button
                                        onClick={handleEmailData}
                                        className="w-full inline-flex items-center justify-center px-4 py-2 rounded-xl bg-slate-900 text-white font-semibold shadow-sm hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-400 transition disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
                                        disabled={emailButtonDisabled}
                                    >
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            className="h-5 w-5 mr-2 shrink-0"
                                            viewBox="0 0 20 20"
                                            fill="currentColor"
                                        >
                                            <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                                            <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                                        </svg>
                                        Email me Data
                                    </button>

                                    <button
                                        onClick={handleRedo}
                                        className="w-full inline-flex items-center justify-center px-4 py-2 rounded-xl bg-slate-900 text-white font-semibold shadow-sm hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-400 transition disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
                                        disabled={expandButtonDisabled}
                                    >
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            className="h-5 w-5 mr-2 shrink-0"
                                            viewBox="0 0 20 20"
                                            fill="currentColor"
                                        >
                                            <path
                                                fillRule="evenodd"
                                                d="M3 3a1 1 0 00-1 1v12a1 1 0 102 0V4a1 1 0 00-1-1zm10.293 9.293a1 1 0 001.414 1.414l3-3a1 1 0 000-1.414l-3-3a1 1 0 10-1.414 1.414L14.586 9H7a1 1 0 100 2h7.586l-1.293 1.293z"
                                                clipRule="evenodd"
                                            />
                                        </svg>
                                        Force Enhance &amp; Expand
                                    </button>

                                    <button
                                        onClick={handleCreateLeads}
                                        className="w-full inline-flex items-center justify-center px-4 py-2 rounded-xl bg-slate-900 text-white font-semibold shadow-sm hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-400 transition disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
                                        disabled={leadButtonDisabled}
                                    >
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            className="h-5 w-5 mr-2 shrink-0"
                                            viewBox="0 0 20 20"
                                            fill="currentColor"
                                        >
                                            <path
                                                fillRule="evenodd"
                                                d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                                                clipRule="evenodd"
                                            />
                                        </svg>
                                        Create CRM Leads
                                    </button>

                                    <button
                                        onClick={() => {
                                            setIsRefreshing(true);
                                            Promise.all([fetchSummaryData(), fetchTableData(currentPage + 1)])
                                                .catch(() => toast.error("Failed to refresh data"))
                                                .finally(() => setIsRefreshing(false));
                                        }}
                                        disabled={isRefreshing}
                                        className="w-full inline-flex items-center justify-center px-4 py-2 rounded-xl bg-slate-900 text-white font-semibold shadow-sm hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-400 transition disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
                                    >
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            className="h-5 w-5 mr-2 shrink-0"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <polyline points="23 4 23 10 17 10" />
                                            <polyline points="1 20 1 14 7 14" />
                                            <path d="M3.51 9a9 9 0 0 1 14.13-3.36" />
                                            <path d="M20.49 15a9 9 0 0 1-14.13 3.36" />
                                        </svg>
                                        {isRefreshing ? "Refreshing..." : "Refresh"}
                                    </button>

                                    <button
                                        onClick={() => (window.location.href = "/seed-insight")}
                                        className="w-full inline-flex items-center justify-center px-4 py-2 rounded-xl bg-slate-900 text-white font-semibold shadow-sm hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-400 transition disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
                                    >
                                        Seed Insight
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            className="h-5 w-5 ml-2"
                                            viewBox="0 0 20 20"
                                            fill="currentColor"
                                        >
                                            <path
                                                fillRule="evenodd"
                                                d="M3 3a1 1 0 00-1 1v12a1 1 0 102 0V4a1 1 0 00-1-1zm10.293 9.293a1 1 0 001.414 1.414l3-3a1 1 0 000-1.414l-3-3a1 1 0 10-1.414 1.414L14.586 9H7a1 1 0 100 2h7.586l-1.293 1.293z"
                                                clipRule="evenodd"
                                            />
                                        </svg>
                                    </button>
                                </div>

                                {/* Filters */}
                                <div className="mb-4">
                                    {/* Mobile (phones only) */}
                                    <div className="sm:hidden space-y-3">
                                        {/* Search */}
                                        <div className="w-full">
                                            <label className="block text-xs font-semibold text-slate-600 mb-1">Search</label>
                                            <input
                                                type="text"
                                                placeholder="Search by domain or company..."
                                                value={searchTerm}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                                className="h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-900 placeholder:text-slate-400
          focus:ring-2 focus:ring-slate-400/40 focus:border-slate-300 transition"
                                            />
                                        </div>

                                        {/* Category */}
                                        <div className="w-full relative">
                                            <FilterPopover
                                                btnText="Category"
                                                filter={categoryFilter}
                                                setFilter={setCategoryFilter}
                                                options={categoryOptions}
                                            />
                                        </div>

                                        {/* Country */}
                                        <div className="w-full relative">
                                            <FilterPopover
                                                btnText="Country"
                                                filter={countryFilter}
                                                setFilter={setCountryFilter}
                                                options={countryOptions}
                                            />
                                        </div>

                                        {/* CRM Status */}
                                        <div className="w-full">
                                            <label className="block text-xs font-semibold text-slate-600 mb-1">CRM Status</label>
                                            <Select
                                                value={selectedCRM}
                                                onChange={setSelectedCRM}
                                                options={crmOptions}
                                                className="react-select-container"
                                                classNamePrefix="react-select"
                                                placeholder="Filter by CRM..."
                                                styles={{
                                                    control: (p) => ({
                                                        ...p,
                                                        minHeight: 44,
                                                        height: 44,
                                                        borderColor: "#e2e8f0",
                                                        boxShadow: "none",
                                                        borderRadius: 12,
                                                        "&:hover": { borderColor: "#cbd5e1" },
                                                    }),
                                                    valueContainer: (p) => ({ ...p, height: 44 }),
                                                    input: (p) => ({ ...p, margin: 0, padding: 0 }),
                                                    option: (p, s) => ({
                                                        ...p,
                                                        backgroundColor: s.isSelected ? "#0f172a" : s.isFocused ? "#f1f5f9" : undefined,
                                                        color: s.isSelected ? "white" : "#0f172a",
                                                    }),
                                                    menu: (p) => ({ ...p, borderRadius: 12, overflow: "hidden" }),
                                                }}
                                            />
                                        </div>
                                    </div>

                                    {/* Desktop / Tablet (original layout preserved) */}
                                    <div className="hidden sm:flex flex-wrap items-center gap-2 sm:gap-3 lg:gap-4">
                                        <div className="flex-[2] min-w-[260px] w-full">
                                            <label className="block text-xs font-semibold text-slate-600 mb-1">Search</label>
                                            <input
                                                type="text"
                                                placeholder="Search by domain or company..."
                                                value={searchTerm}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                                className="h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm sm:text-base text-slate-900 placeholder:text-slate-400
          focus:ring-2 focus:ring-slate-400/40 focus:border-slate-300 transition"
                                            />
                                        </div>

                                        <div className="flex items-end">
                                            <FilterPopover
                                                btnText="Category"
                                                filter={categoryFilter}
                                                setFilter={setCategoryFilter}
                                                options={categoryOptions}
                                            />
                                        </div>

                                        <div className="flex items-end">
                                            <FilterPopover
                                                btnText="Country"
                                                filter={countryFilter}
                                                setFilter={setCountryFilter}
                                                options={countryOptions}
                                            />
                                        </div>

                                        <div className="w-56 sm:w-64">
                                            <label className="block text-xs font-semibold text-slate-600 mb-1">CRM Status</label>
                                            <Select
                                                value={selectedCRM}
                                                onChange={setSelectedCRM}
                                                options={crmOptions}
                                                className="react-select-container"
                                                classNamePrefix="react-select"
                                                placeholder="Filter by CRM..."
                                                styles={{
                                                    control: (p) => ({
                                                        ...p,
                                                        minHeight: 44,
                                                        height: 44,
                                                        borderColor: "#e2e8f0",
                                                        boxShadow: "none",
                                                        borderRadius: 12,
                                                        "&:hover": { borderColor: "#cbd5e1" },
                                                    }),
                                                    valueContainer: (p) => ({ ...p, height: 44 }),
                                                    input: (p) => ({ ...p, margin: 0, padding: 0 }),
                                                    option: (p, s) => ({
                                                        ...p,
                                                        backgroundColor: s.isSelected ? "#0f172a" : s.isFocused ? "#f1f5f9" : undefined,
                                                        color: s.isSelected ? "white" : "#0f172a",
                                                    }),
                                                    menu: (p) => ({ ...p, borderRadius: 12, overflow: "hidden" }),
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Table */}
                                <LeadsTable
                                    records={displayedRecords}
                                    filteredCount={filteredCount}
                                    absoluteTotal={absoluteTotal}
                                    sortField={sortField}
                                    sortOrder={sortOrder}
                                    onSort={handleSort}
                                    formatDate={formatDate}
                                    onOpenDetails={(rec) => {
                                        setRecordDetails(rec);
                                        setIsDetailsModalOpen(true);
                                    }}
                                    onOpenUpdate={(rec) => {
                                        setRecordToUpdate(rec);
                                        setIsUpdateModalOpen(true);
                                    }}
                                    onOpenDelete={(rec) => {
                                        setRecordToDelete(rec);
                                        setIsDeleteModalOpen(true);
                                    }}
                                    onOpenLinkedContext={openLinkedContactsDialog}
                                    onAddToBlacklist={addDomainToBlacklist}
                                    onAddToQueue={handleSingleRedo}
                                    onCreateLead={handleCreateSingleLead}
                                    expandDisabled={expandButtonDisabled}
                                    leadDisabled={leadButtonDisabled}
                                    blacklistBusy={blacklistBusy}
                                    pageCount={pageCount}
                                    currentPage={currentPage}
                                    onPageChange={setCurrentPage}
                                    recordsPerPage={recordsPerPage}
                                />
                            </div>
                        </section>

                        {/* Update Modal */}
                        <ReactModal
                            isOpen={isUpdateModalOpen}
                            onRequestClose={() => setIsUpdateModalOpen(false)}
                            className="bg-white p-4 sm:p-6 rounded-2xl shadow-xl w-[min(40rem,calc(100vw-2rem))] max-h-[80vh] overflow-y-auto outline-none border border-slate-200"
                            overlayClassName="fixed inset-0 bg-black/40 flex justify-center items-center px-3"
                        >
                            <div className="flex items-start justify-between gap-3 mb-4">
                                <div>
                                    <h2 className="text-lg sm:text-xl font-extrabold text-slate-900">Update Record</h2>
                                    <p className="text-sm text-slate-600 mt-1">Edit fields and save changes.</p>
                                </div>
                                <button
                                    onClick={() => setIsUpdateModalOpen(false)}
                                    className="rounded-xl px-3 py-2 text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition"
                                    aria-label="Close"
                                    type="button"
                                >
                                    X
                                </button>
                            </div>

                            {renderFields(recordToUpdate, true)}

                            <div className="mt-4 flex flex-col sm:flex-row justify-end gap-2 sm:gap-3">
                                <button
                                    onClick={() => setIsUpdateModalOpen(false)}
                                    className="py-2 px-4 rounded-xl bg-slate-100 font-semibold text-slate-800 hover:bg-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-300 transition"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleUpdateRecord}
                                    className="py-2 px-4 rounded-xl bg-amber-600 font-semibold text-white shadow-sm hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-amber-300 transition"
                                >
                                    Update
                                </button>
                            </div>
                        </ReactModal>

                        {/* Details Modal */}
                        <ReactModal
                            isOpen={isDetailsModalOpen}
                            onRequestClose={() => setIsDetailsModalOpen(false)}
                            className="bg-white p-4 sm:p-6 rounded-2xl shadow-xl w-[min(44rem,calc(100vw-2rem))] max-h-[80vh] overflow-y-auto outline-none border border-slate-200"
                            overlayClassName="fixed inset-0 bg-black/40 flex justify-center items-center px-3"
                        >
                            <div className="flex items-start justify-between gap-3 mb-4">
                                <div>
                                    <h2 className="text-lg sm:text-xl font-extrabold text-slate-900">Record Details</h2>
                                    <p className="text-sm text-slate-600 mt-1">Read-only view of the record.</p>
                                </div>
                                <button
                                    onClick={() => setIsDetailsModalOpen(false)}
                                    className="rounded-xl px-3 py-2 text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition"
                                    aria-label="Close"
                                    type="button"
                                >
                                    X
                                </button>
                            </div>

                            {renderFields(recordDetails, false, true)}
                            <div className="mt-4 flex flex-col sm:flex-row justify-end gap-2 sm:gap-3">
                                <button
                                    onClick={() => openLinkedContactsDialog(recordDetails)}
                                    className="py-2 px-4 rounded-xl bg-slate-900 font-semibold text-white shadow-sm hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-400/60 transition"
                                    type="button"
                                >
                                    Linked Contacts
                                </button>
                                <button
                                    onClick={() => setIsDetailsModalOpen(false)}
                                    className="py-2 px-4 rounded-xl bg-slate-100 font-semibold text-slate-800 hover:bg-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-300 transition"
                                    type="button"
                                >
                                    Close
                                </button>
                            </div>
                        </ReactModal>

                        {/* Delete Modal */}
                        <ReactModal
                            isOpen={isDeleteModalOpen}
                            onRequestClose={() => setIsDeleteModalOpen(false)}
                            className="bg-white p-4 sm:p-6 rounded-2xl shadow-xl w-[min(28rem,calc(100vw-2rem))] outline-none border border-slate-200"
                            overlayClassName="fixed inset-0 bg-black/40 flex justify-center items-center px-3"
                        >
                            <div className="flex items-start justify-between gap-3 mb-3">
                                <h2 className="text-lg sm:text-xl font-extrabold text-slate-900">Confirm Deletion</h2>
                                <button
                                    onClick={() => setIsDeleteModalOpen(false)}
                                    className="rounded-xl px-3 py-2 text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition"
                                    aria-label="Close"
                                    type="button"
                                >
                                    X
                                </button>
                            </div>

                            <p className="text-slate-700 mb-5 text-sm sm:text-base">
                                Are you sure you want to delete this record?
                            </p>

                            <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3">
                                <button
                                    onClick={() => setIsDeleteModalOpen(false)}
                                    className="py-2 px-4 rounded-xl bg-slate-100 text-slate-800 font-semibold hover:bg-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-300 transition"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleDeleteRecord}
                                    className="py-2 px-4 rounded-xl bg-red-600 text-white font-semibold shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-300 transition"
                                >
                                    Delete
                                </button>
                            </div>
                        </ReactModal>

                        <LinkedContextDialog
                            isOpen={isLinkedDialogOpen}
                            onClose={() => setIsLinkedDialogOpen(false)}
                            title="Linked Contacts"
                            loading={linkedLoading}
                            record={linkedRecord}
                            contacts={linkedContacts}
                            onAddBlacklist={addDomainToBlacklist}
                            blacklistBusy={blacklistBusy}
                        />
                    </div>
                )}
            </div>
        </div>
    );

    function renderFields(record, updateMode, readOnly = false) {
        const baseFields = [
            { label: "Domain", name: "domain", isLink: true },
            { label: "Company Name", name: "company_name" },
            { label: "Summary", name: "summary" },
            { label: "Emails", name: "emails" },
            { label: "Phone", name: "phone" },
            { label: "LinkedIn", name: "linkedin", isLink: true },
            { label: "Street Address", name: "street_address" },
            { label: "City", name: "city" },
            { label: "State/Province", name: "state_province" },
            { label: "Zip/Postal Code", name: "zip_postal_code" },
            { label: "Country", name: "country" },
            { label: "Business Category", name: "business_category" },
            { label: "Keywords", name: "keywords" },
            { label: "Search Terms", name: "search_terms" },
            { label: "Relevance", name: "relevance" },
            { label: "Research Similar Flag", name: "research_similar_flag" },
        ];
        const detailOnlyFields = [
            { label: "Leverage Score", name: "leverage_score" },
            { label: "Outreach Score", name: "outreach_score" },
            { label: "Missed Opportunity Risk", name: "missed_opportunity_risk" },
            { label: "Leverage Enhancement Queue Status", name: "leverage_enhancement_queue_status" },
        ];
        const fields = readOnly ? [...baseFields, ...detailOnlyFields] : baseFields;

        return (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {fields.map((field) => {
                    let value = record[field.name] ?? "";
                    if (field.isDate) value = formatDate(value);

                    if (readOnly && field.isLink && value) {
                        return (
                            <div key={field.name} className="sm:col-span-2">
                                <label className="block text-xs font-semibold text-slate-600 mb-1">{field.label}</label>
                                <a
                                    href={value.startsWith("http") ? value : `https://${value}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="block w-full break-all rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-slate-900 underline decoration-slate-300 underline-offset-4 hover:decoration-slate-500"
                                >
                                    {value}
                                </a>
                            </div>
                        );
                    }

                    return (
                        <div key={field.name} className={field.name === "summary" ? "sm:col-span-2" : ""}>
                            <label className="block text-xs font-semibold text-slate-600 mb-1">{field.label}</label>
                            <input
                                name={field.name}
                                type="text"
                                value={value}
                                onChange={(e) => !readOnly && handleInputChange(e, updateMode)}
                                readOnly={readOnly || field.readOnly}
                                className={[
                                    "w-full rounded-xl border px-3 py-2 text-sm text-slate-900 transition",
                                    readOnly || field.readOnly
                                        ? "border-slate-200 bg-slate-100 text-slate-500 cursor-not-allowed"
                                        : "border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-slate-400/40 focus:border-slate-300",
                                ].join(" ")}
                            />
                        </div>
                    );
                })}
            </div>
        );
    }
}

function FilterPopover({ btnText, filter, setFilter, options }) {
    const [open, setOpen] = useState(false);
    const [draftOp, setDraftOp] = useState(filter.op);
    const [draftVals, setDraftVals] = useState(filter.values || []);
    const ref = useRef(null);

    useEffect(() => {
        if (open) {
            setDraftOp(filter.op);
            setDraftVals(filter.values || []);
        }
    }, [open, filter.op, filter.values]);

    useEffect(() => {
        const onDoc = (e) => {
            if (open && ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, [open]);

    const disabled = draftOp === "has" || draftOp === "not_has";

    return (
        <div className="relative">
            <label className="block text-xs font-semibold text-slate-600 mb-1">Filter</label>
            <button
                type="button"
                className="h-11 inline-flex items-center justify-center rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-800 hover:bg-slate-50 hover:border-slate-300 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60"
                onClick={() => setOpen((o) => !o)}
            >
                Filter: {btnText}
                <span className="ml-2 text-slate-400" aria-hidden="true">
                    v
                </span>
            </button>

            {open && (
                <div
                    ref={ref}
                    onMouseDown={(e) => e.stopPropagation()}
                    className="absolute right-0 mt-2 w-80 rounded-2xl bg-white border border-slate-200 shadow-xl p-4 z-20"
                >
                    <div className="flex items-center justify-between mb-3">
                        <div className="font-extrabold text-slate-900">Filter by {btnText}</div>
                        <button
                            className="rounded-xl px-3 py-2 text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition"
                            onClick={() => setOpen(false)}
                            type="button"
                            aria-label="Close"
                        >
                            X
                        </button>
                    </div>

                    <div className="space-y-3">
                        <Select
                            value={OP_OPTIONS.find((o) => o.value === draftOp)}
                            onChange={(op) => setDraftOp(op.value)}
                            options={OP_OPTIONS}
                            classNamePrefix="react-select"
                            placeholder="Equals"
                            styles={{
                                control: (p) => ({
                                    ...p,
                                    minHeight: 44,
                                    height: 44,
                                    borderColor: "#e2e8f0",
                                    boxShadow: "none",
                                    borderRadius: 12,
                                    "&:hover": { borderColor: "#cbd5e1" },
                                }),
                                option: (p, s) => ({
                                    ...p,
                                    backgroundColor: s.isSelected ? "#0f172a" : s.isFocused ? "#f1f5f9" : undefined,
                                    color: s.isSelected ? "white" : "#0f172a",
                                }),
                                menu: (p) => ({ ...p, borderRadius: 12, overflow: "hidden" }),
                            }}
                        />

                        <Select
                            isMulti
                            closeMenuOnSelect={false}
                            hideSelectedOptions={false}
                            isDisabled={disabled}
                            value={draftVals}
                            onChange={(vals) => setDraftVals(vals || [])}
                            options={options}
                            classNamePrefix="react-select"
                            placeholder="Select..."
                            isClearable={false}
                            styles={{
                                control: (p) => ({
                                    ...p,
                                    minHeight: 44,
                                    borderColor: "#e2e8f0",
                                    boxShadow: "none",
                                    borderRadius: 12,
                                    "&:hover": { borderColor: "#cbd5e1" },
                                    opacity: disabled ? 0.65 : 1,
                                }),
                                option: (p, s) => ({
                                    ...p,
                                    backgroundColor: s.isSelected ? "#0f172a" : s.isFocused ? "#f1f5f9" : undefined,
                                    color: s.isSelected ? "white" : "#0f172a",
                                }),
                                menu: (p) => ({ ...p, borderRadius: 12, overflow: "hidden" }),
                            }}
                        />
                    </div>

                    <div className="mt-4 flex justify-end gap-2">
                        <button
                            className="px-3 py-2 text-sm font-semibold rounded-xl bg-slate-100 text-slate-800 hover:bg-slate-200 transition focus:outline-none focus:ring-2 focus:ring-slate-300"
                            onClick={() => setDraftVals([])}
                            type="button"
                        >
                            Clear
                        </button>
                        <button
                            className="px-4 py-2 text-sm font-semibold rounded-xl bg-slate-900 text-white hover:bg-slate-800 transition focus:outline-none focus:ring-2 focus:ring-slate-400/60"
                            onClick={() => {
                                setFilter({ op: draftOp, values: draftVals });
                                setOpen(false);
                            }}
                            type="button"
                        >
                            Apply
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

